import { SupportedHostname } from './types';
import { Timeout } from './Timeout';
import { formatUrl } from './formatUrl';
import { get } from 'lodash';
// Note that the store is a singleton (read: global), and care should be taken
// when reusing/serializing in other places.
import { store, EnabledConfig } from './ContextStore';
import { heliosOmnitureTracking } from './HeliosOmnitureTracking';
export const resetStore = store.reset;
export const updateDomainScheme = store.updateDomainScheme;
export const updateConfigFlags = store.updateConfigFlags;
// This function makes optional flags like acceptJson and acceptErrors, among
// others strictly required. We can rely on simpler boolean flags down the
// line, rather than check explicitly for `undefined` etc. In cases like
// acceptJson, the definition of `undefined` isn't `falsy` and will avoid that
// pitfall.
const normalizeFlags = (config) => {
    const acceptJson = typeof config.acceptJson !== 'undefined' ? config.acceptJson : true;
    // TODO: This is probably equivalent to Boolean(config.acceptErrors)
    const acceptErrors = typeof config.acceptErrors !== 'undefined' ? config.acceptErrors : false;
    const normalizedFlags = {
        acceptJson,
        acceptErrors,
        useNginxCache: false,
        multipart: Boolean(config.multipart),
        sendToSentry: Boolean(config.sendToSentry)
    };
    return Object.assign({}, config, normalizedFlags);
};
function cloneWithContext({ context }) {
    const hostname = store.getHostname(context);
    let updatedContext = Object.assign(Object.assign({}, context), { hostname });
    updatedContext.url = formatUrl(updatedContext);
    updatedContext.timeout = new Timeout(updatedContext);
    return updatedContext;
}
// TODO: Add support for hostname overrides. If someone passes a
// hostname explicitly, it has to win no matter what the contextStore
// computes it to.
function cloneWithConfig({ config, body }) {
    const normalizedConfig = normalizeFlags(config);
    const hostname = store.getHostname(normalizedConfig);
    // TODO: Trim the /api prefix if it's present. Eventually we'll have to
    // replace all the prefixes in code. The only way to do this without that
    // change is to match the primary hostname to the newer formats.
    const pathname = normalizedConfig.pathname;
    const updatedConfig = Object.assign(Object.assign({}, normalizedConfig), { hostname, pathname });
    return Object.assign(Object.assign({}, updatedConfig), { url: formatUrl(updatedConfig), body, timeout: new Timeout(updatedConfig) });
}
export function cloneWith({ context, config, body }) {
    if (__DEBUG_ENV__ && !context && !config) {
        throw new Error('Either context or config is required to clone');
    }
    if (context) {
        return cloneWithContext({ context });
    }
    return cloneWithConfig({ config: config, body });
}
const isDCRedirectResponse = (obj) => {
    return ('ERROR_CODE' in obj &&
        obj.ERROR_CODE === 2000 &&
        'META_INFO' in obj &&
        'dcInfo' in obj.META_INFO &&
        obj.META_INFO.dcInfo.id);
};
export function updateHostnameFromPromise({ promise, context }) {
    return promise.then(body => {
        if (__DEBUG_ENV__) {
            // tslint:disable:no-console
            console.log('ApiCaller: DC change ', body);
        }
        if (isDCRedirectResponse(body)) {
            const dc = body.META_INFO.dcInfo.id;
            heliosOmnitureTracking.set(dc);
            store.setHostname(context, dc);
        }
        return Promise.resolve(cloneWith({ context }));
    });
}
export const logInFdp = (requestContext) => {
    store.logInFdp(requestContext);
    return requestContext;
};
export function logAbv2InfoInFdp(response) {
    store.logABv2InfoInFdp(response);
}
export function getConfigValues(config) {
    const globalConfig = get(config, 'data.globalConfig');
    const flags = {
        supportHeliosRedirection: get(globalConfig, 'supportHeliosRedirection', false),
        enableABV2Changes: get(globalConfig, 'enableABV2Changes', false)
    };
    if (__DEBUG_ENV__) {
        console.log(`Config flag values required :`, flags);
    }
    return flags;
}
export function updateApiContextBasedOnConfig(appConfig) {
    const flags = getConfigValues(appConfig);
    // helios domain setter
    toggleHeliosDomain(flags.supportHeliosRedirection);
    // store copy of flags in context store
    updateConfigFlags(flags);
}
export function toggleHeliosDomain(supportHeliosRedirection) {
    // For dev and stage environments, where we might have to configure custom
    // api host endpoints, avoid toggling based on app config and prefer
    // whatever was set as the enabled configuration to be picked up.
    if (__DEBUG_ENV__)
        return;
    if (supportHeliosRedirection) {
        updateDomainScheme(SupportedHostname.ROME_COM);
    }
    else {
        // The as here is fine the hostname will be populated from the
        // environment variable and defaults to www.
        updateDomainScheme(EnabledConfig);
    }
}
